import { memo, useState } from "react";

import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { StaleFinding } from "stores/DashboardStore";

import { GenericObject } from "utility/misc";
import { FOTS, SUTS } from "utility/typescript";

import {
  DataGridStyle,
  FindingsColumns,
  FindingsColumnsLoading,
  LoadingRows,
  PaperStyle,
} from "./data";

// -----------------------------------------------------------------------------

const PageSize = 20;
const PageSizeOptions = [PageSize];
const DataComponents: GenericObject = {
  LoadingOverlay: NullComponent,
};
enum SORT_BY {
  ASCENDING = "ASCENDING",
  DESCENDING = "DESCENDING",
}

// -----------------------------------------------------------------------------

const StaleFindingsDataTable = memo(function StaleFindingsDataTable({
  data,
  isLoading,
}: StaleFindingsDataTableProps): JSX.Element {
  const [sortBy, setSortBy] = useState<SORT_BY>(SORT_BY.ASCENDING);

  //
  const handleChange = (event: SUTS<{ target: { value: SORT_BY } } | null>) => {
    setSortBy(event?.target?.value ?? SORT_BY.ASCENDING);
  };

  //
  return (
    <>
      <Grid
        container
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Grid item>
          <Typography variant="h6" fontWeight="bold">
            Stale Findings
          </Typography>
          <Typography gutterBottom variant="caption" fontWeight="light">
            Showing data since {dayjs().format("MM/DD/YYYY")}
          </Typography>
        </Grid>
        <Grid item paddingTop={0}>
          <Select
            id="sort-by-select"
            value={sortBy}
            inputProps={{ "aria-label": "Sort by order" }}
            onChange={handleChange as FOTS}
          >
            <MenuItem value={SORT_BY.ASCENDING}>Sort By: Ascending</MenuItem>
            <MenuItem value={SORT_BY.DESCENDING}>Sort By: Descending</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Paper elevation={1} sx={PaperStyle}>
        <DataGrid
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          density="compact"
          getRowId={getRowId}
          rows={isLoading ? LoadingRows : data}
          rowCount={data?.length ?? 0}
          columns={isLoading ? FindingsColumnsLoading : FindingsColumns}
          components={DataComponents}
          pageSize={PageSize}
          rowsPerPageOptions={PageSizeOptions}
          checkboxSelection={false}
          sx={DataGridStyle}
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: "create_time",
                  sort: sortBy === SORT_BY.ASCENDING ? "asc" : "desc",
                },
              ],
            },
          }}
          onSortModelChange={(model: Array<{ sort: string }>) => {
            setSortBy(
              model[0].sort === "asc" ? SORT_BY.ASCENDING : SORT_BY.DESCENDING
            );
          }}
          sortingOrder={["asc", "desc"]}
        />
      </Paper>
    </>
  );
});

export default StaleFindingsDataTable;

// -----------------------------------------------------------------------------

function getRowId(row: { create_time: string }): string {
  return `${row.create_time}-${Math.random()}`;
}

function NullComponent() {
  return <></>;
}

interface StaleFindingsDataTableProps {
  data: StaleFinding[];
  isLoading: boolean;
}
